import { useQuery } from "@tanstack/react-query";
import { useAuth, normalizePhone } from "@mydailydirect/auth";
import { useMyDailyDirectClient } from "@mydailydirect/app-kit";
import type { RetailerDTO } from "@mydailydirect/api-client";
import { isSmallKiranaOrExempt } from "@mydailydirect/api-client";

export interface UseRetailerIdResult {
  retailerId: number | null;
  retailer: RetailerDTO | null;
  buyerName: string;
  buyerPhone: string;
  loading: boolean;
  isVerified: boolean;
  isApproved: boolean;
  hasShopProof: boolean;
  hasGstNumber: boolean;
  isSmallKirana: boolean;
}

/**
 * Resolves the signed-in retailer's numeric id, shop entity, and verification status.
 * Uses a fast single-retailer lookup (`/retailers/{id}`) whenever `me.retailerId` is linked,
 * avoiding downloading the full multi-megabyte retailers list on mobile connections.
 */
export function useRetailerId(): UseRetailerIdResult {
  const { session } = useAuth();
  const client = useMyDailyDirectClient();

  const usersQuery = useQuery({
    queryKey: ["users"],
    queryFn: () => client.users.list(),
    enabled: Boolean(session),
  });

  const sessionDigits = normalizePhone(session?.phone || session?.userId);

  const me = usersQuery.data?.find(
    (u) =>
      u.username.toLowerCase() === (session?.userId ?? "").toLowerCase() ||
      (sessionDigits.length > 0 && normalizePhone(u.phone) === sessionDigits) ||
      (sessionDigits.length > 0 && normalizePhone(u.username) === sessionDigits),
  );

  // Fast single-retailer fetch when me.retailerId is known
  const myRetailerQuery = useQuery({
    queryKey: ["retailer", me?.retailerId],
    queryFn: () => client.retailers.get(me!.retailerId!),
    enabled: Boolean(session && me?.retailerId),
  });

  // Only fetch the full retailers list if the user record has no linked retailerId
  const needsFullList = Boolean(
    session && usersQuery.isFetched && (!me?.retailerId || myRetailerQuery.isError),
  );
  const retailersQuery = useQuery({
    queryKey: ["retailers"],
    queryFn: () => client.retailers.list(),
    enabled: needsFullList,
  });

  const isStaffOrAdmin = Boolean(
    session?.roles.some((r) => r === "ADMIN" || r === "RM" || r === "TRADER"),
  );

  let retailer: RetailerDTO | null = null;
  let retailerId: number | null = null;

  if (me?.retailerId) {
    retailerId = me.retailerId;
    retailer =
      myRetailerQuery.data ??
      retailersQuery.data?.find((r) => r.id === me.retailerId) ??
      null;
  } else {
    const matchedRetailer = retailersQuery.data?.find(
      (r) => sessionDigits.length > 0 && normalizePhone(r.phone) === sessionDigits,
    );
    if (matchedRetailer?.id) {
      retailerId = matchedRetailer.id;
      retailer = matchedRetailer;
    } else if (isStaffOrAdmin && retailersQuery.data && retailersQuery.data.length > 0) {
      const fallbackRetailer =
        retailersQuery.data.find((r) => r.status === "APPROVED") ?? retailersQuery.data[0];
      retailerId = fallbackRetailer?.id ?? null;
      retailer = fallbackRetailer ?? null;
    }
  }

  const resolvedName =
    retailer?.name?.trim() ||
    me?.fullName?.trim() ||
    session?.businessName?.trim() ||
    session?.fullName?.trim() ||
    retailer?.contactPerson?.trim() ||
    (sessionDigits ? `Shop (${sessionDigits})` : "Retailer");

  const resolvedPhone =
    retailer?.phone?.trim() ||
    me?.phone?.trim() ||
    session?.phone?.trim() ||
    sessionDigits ||
    "";

  // Ensure retailer object always carries the resolved buyer name even while photoUrl is loading
  if (!retailer && session) {
    retailer = {
      id: retailerId ?? 0,
      name: resolvedName,
      phone: resolvedPhone,
      city: "Bengaluru",
      addressLine: "Bengaluru",
      status: isStaffOrAdmin ? "APPROVED" : "PENDING",
    };
  } else if (retailer && (!retailer.name || retailer.name.trim() === "")) {
    retailer = {
      ...retailer,
      name: resolvedName,
    };
  }

  // Strict ordering compliance:
  // Retailers require valid 15-character GSTIN, shop proof document, and manual admin APPROVED status to order.
  const isSmallKirana = isSmallKiranaOrExempt(retailer);
  const hasShopProof = isSmallKirana || Boolean(retailer?.photoUrl && retailer.photoUrl.trim().length > 0);
  const hasGstNumber = isSmallKirana || Boolean(retailer?.gstNumber && retailer.gstNumber.trim().length >= 15);
  const isApproved = retailer?.status === "APPROVED";
  const isVerified = Boolean(retailer !== null && isApproved && (isSmallKirana || (hasShopProof && hasGstNumber)));

  return {
    retailerId,
    retailer,
    buyerName: resolvedName,
    buyerPhone: resolvedPhone,
    loading: usersQuery.isLoading || myRetailerQuery.isLoading || (needsFullList && retailersQuery.isLoading),
    isVerified,
    isApproved,
    hasShopProof,
    hasGstNumber,
    isSmallKirana,
  };
}
